var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { Edit as RaEdit, EditBase, EditView as RaEditView, useCheckMinimumRequiredProps, usePermissions, useRecordContext, useResourceContext, useTranslate, } from 'react-admin';
import { Button, Box } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import History from '@mui/icons-material/History';
import { canAccess } from '../canAccess';
import { EditActions } from './EditActions';
/**
 * Replacement for react-admin's Edit that adds RBAC control to actions
 *
 * Users must have the 'edit' permission on the resource and record to see the Edit view.
 * Users must have the 'show' permission on the resource and record to see the ShowButton.
 * Users must have the 'clone' permission on the resource and record to see the CloneButton.
 *
 * @example
 * import { Edit } from '@react-admin/ra-rbac';
 *
 * const authProvider = {
 *      // ...
 *      getPermissions: () => Promise.resolve([
 *          { action: ['list', 'edit', 'clone'], resource: 'products' },
 *      ]),
 * };
 *
 * export const PostEdit = () => (
 *     <Edit>
 *         ...
 *     </Edit>
 * );
 * // user will see the clone button but not the show button
 */
export var Edit = function (props) {
    useCheckMinimumRequiredProps('Edit', ['children'], props);
    var resource = props.resource, id = props.id, mutationMode = props.mutationMode, mutationOptions = props.mutationOptions, queryOptions = props.queryOptions, redirect = props.redirect, transform = props.transform, disableAuthentication = props.disableAuthentication, rest = __rest(props, ["resource", "id", "mutationMode", "mutationOptions", "queryOptions", "redirect", "transform", "disableAuthentication"]);
    return (React.createElement(EditBase, { resource: resource, id: id, mutationMode: mutationMode, mutationOptions: mutationOptions, queryOptions: queryOptions, redirect: redirect, transform: transform, disableAuthentication: disableAuthentication },
        React.createElement(EditView, __assign({ actions: React.createElement(EditActions, null) }, rest))));
};
Edit.propTypes = RaEdit.propTypes;
var EditView = function (props) {
    var _a;
    var resource = useResourceContext();
    var record = useRecordContext();
    var _b = usePermissions(), isLoading = _b.isLoading, permissions = _b.permissions;
    if (isLoading) {
        return null;
    }
    if (!canAccess({
        permissions: permissions,
        action: 'edit',
        resource: resource,
        record: record,
    })) {
        return (_a = props.unauthorized) !== null && _a !== void 0 ? _a : React.createElement(DefaultUnauthorizedView, null);
    }
    return React.createElement(RaEditView, __assign({}, props));
};
var DefaultUnauthorizedView = function () {
    var translate = useTranslate();
    return (React.createElement(Box, { sx: function (theme) {
            var _a;
            return (_a = {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                },
                _a[theme.breakpoints.down('md')] = {
                    padding: '1em',
                },
                _a.fontFamily = 'Roboto, sans-serif',
                _a.opacity = 0.5,
                _a['& h1'] = {
                    display: 'flex',
                    alignItems: 'center',
                },
                _a);
        } },
        React.createElement("h1", { role: "alert" },
            React.createElement(LockIcon, { sx: {
                    width: '2em',
                    height: '2em',
                    marginRight: '0.5em',
                } }),
            translate('ra-rbac.page.unauthorized')),
        React.createElement("div", null, translate('ra-rbac.message.unauthorized')),
        React.createElement(Box, { sx: {
                marginTop: '2em',
            } },
            React.createElement(Button, { variant: "contained", startIcon: React.createElement(History, null), onClick: goBack }, translate('ra.action.back')))));
};
function goBack() {
    window.history.go(-1);
}
